// src/utils/roomUtils.js

/**
 * Format price to display string
 */
export const formatPrice = (price) => {
  if (price == null) return '—';
  if (price === 0) return 'Free';
  return `$${price.toFixed(4)}`;
};

/**
 * Format price in SOL
 */
export const formatPriceSOL = (price) => {
  if (price == null) return '—';
  if (price === 0) return 'Free';
  return `${price.toFixed(4)} SOL`;
};

/**
 * Get seats remaining
 */
export const getSeatsRemaining = (room) => {
  if (!room) return 0;
  return Math.max(0, room.seatsLeft || 0);
};

/**
 * Get seats sold
 */
export const getSeatsSold = (room) => {
  if (!room) return 0;
  return Math.max(0, (room.totalSeats || 0) - (room.seatsLeft || 0));
};

/**
 * Get seat fill percentage
 */
export const getSeatFillPercent = (room) => {
  if (!room || !room.totalSeats) return 0;
  const sold = getSeatsSold(room);
  return Math.round((sold / room.totalSeats) * 100);
};

/**
 * Check if room is sold out
 */
export const isSoldOut = (room) => {
  if (!room) return false;
  return room.seatsLeft === 0;
};

/**
 * Check if room is almost full (10 or fewer seats)
 */
export const isAlmostFull = (room) => {
  if (!room) return false;
  return room.seatsLeft > 0 && room.seatsLeft <= 10;
};

/**
 * Check if room is live
 */
export const isRoomLive = (room) => {
  return room?.status === 'live';
};

/**
 * Check if room is scheduled
 */
export const isRoomScheduled = (room) => {
  return room?.status === 'scheduled';
};

/**
 * Check if room is ended
 */
export const isRoomEnded = (room) => {
  return room?.status === 'ended';
};

/**
 * Grace window: a scheduled room stays "upcoming" for this long past its
 * scheduled_at (creator may start late) before being treated as past.
 */
export const ROOM_PAST_GRACE_MS = 2 * 60 * 60 * 1000; // 2h

/**
 * A room is "past" when it ended, or when it's still `scheduled` but its
 * start time is more than the grace window behind us (i.e. it never went
 * live and realistically won't). Live rooms are never past.
 */
export const isRoomPast = (room, graceMs = ROOM_PAST_GRACE_MS) => {
  if (!room) return false;
  if (room.status === 'ended' || room.status === 'cancelled') return true;
  if (room.status === 'live') return false;
  const scheduledAt = room.scheduledAt ?? room.scheduled_at;
  if (!scheduledAt) return false;
  const t = new Date(scheduledAt).getTime();
  return Number.isFinite(t) && t + graceMs < Date.now();
};

/**
 * Get room status object
 */
export const getRoomStatus = (room) => {
  return {
    isLive: isRoomLive(room),
    isScheduled: isRoomScheduled(room),
    isEnded: isRoomEnded(room),
    isSoldOut: isSoldOut(room),
    isAlmostFull: isAlmostFull(room)
  };
};

/**
 * Format viewer count
 */
export const formatViewers = (count) => {
  if (count == null) return '0';
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toLocaleString();
};

/**
 * Format duration in minutes to readable string
 */
export const formatDuration = (minutes) => {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Format time ago
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

/**
 * Format scheduled time to relative string
 */
export const formatScheduledTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 0) return 'Started';
  if (diffMins < 60) return `In ${diffMins}m`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `In ${diffHours}h`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `In ${diffDays} days`;

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get availability text for room
 */
export const getAvailabilityText = (room) => {
  if (isSoldOut(room)) return 'Sold out';
  if (isAlmostFull(room)) return `Only ${room.seatsLeft} left!`;
  return `${room.seatsLeft} seats left`;
};

/**
 * Get availability color class
 */
export const getAvailabilityColor = (room) => {
  if (isSoldOut(room)) return 'text-zinc-600';
  if (isAlmostFull(room)) return 'text-[#A3E635]';
  return 'text-zinc-500';
};

export default {
  formatPrice,
  formatPriceSOL,
  getSeatsRemaining,
  getSeatsSold,
  getSeatFillPercent,
  isSoldOut,
  isAlmostFull,
  isRoomLive,
  isRoomScheduled,
  isRoomEnded,
  isRoomPast,
  ROOM_PAST_GRACE_MS,
  getRoomStatus,
  formatViewers,
  formatDuration,
  formatTimeAgo,
  formatScheduledTime,
  getAvailabilityText,
  getAvailabilityColor
};